import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '../index';
import { FilterParams, IBase, QueryParams } from 'models';
import { IPost } from './postManagementService';
const { REACT_APP_API_URL } = process.env;

export interface IBaseCategory {
  readonly id: number;
  readonly name: string;
  readonly type: string;
}

export interface ICategoryType extends IBaseCategory { }

export interface ISubCategory extends IBaseCategory {
  readonly subCategoryTypes?: ICategoryType[];
}

export interface ICategory extends IBaseCategory {
  readonly icon: string;
  readonly type: 'product' | 'service';
  readonly subCategories?: ISubCategory[];
}

export const categoryManagementServiceApi = createApi({
  reducerPath: 'categoryManagementServiceApi',
  tagTypes: ['Categories'],
  baseQuery: fetchBaseQuery({
    baseUrl: REACT_APP_API_URL,
    prepareHeaders: (headers, { getState }) => {
      const state = getState() as RootState;
      const token = state.auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getCategories: builder.query<Array<ICategory>, void>({
      query: () => ({
        url: `api/categories`,
        method: 'GET',
      }),
      providesTags: ['Categories'],
    }),
    getCategory: builder.query<ICategory, number>({
      query: (categoryId) => ({
        url: `api/category/${categoryId}`,
        method: 'GET',
      }),
      providesTags: ['Categories'],
    }),
    getCategoryMaxPrice: builder.query<number, number>({
      query: (categoryId) => ({
        url: `api/category-posts-max-price/${categoryId}`,
        method: 'GET',
      }),
      providesTags: ['Categories'],
    }),
    getCategoryPost: builder.query<
      IBase<IPost>,
      { categoryId: number; pagination: QueryParams }
    >({
      query: ({ categoryId, pagination }) => {
        const filterParams = new URLSearchParams();

        if (pagination.filter) {
          Object.entries(pagination.filter as FilterParams).forEach(
            ([key, value]) => {
              if (value !== undefined && value !== '') {
                filterParams.append(key, value.toString());
              }
            }
          );
        }
        const queryString = filterParams.toString().trim();
        return {
          url: `api/category-posts/${categoryId}?page=${pagination.page}&limit=${pagination.limit}&${queryString}`,
          method: 'GET',
        };
      },
      providesTags: ['Categories'],
    }),
  }),
});

export const {
  useGetCategoriesQuery,
  useGetCategoryQuery,
  useGetCategoryPostQuery,
  useLazyGetCategoryPostQuery,
  useGetCategoryMaxPriceQuery
} = categoryManagementServiceApi;
